import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useTranslation, Trans } from 'react-i18next';
import { Btn, Card, Avatar, QGlyph, ServerBadge, Icons, PublicHeader, LoadingScreen, ErrorScreen } from './ui';
import { TranslateText } from './TranslateText';

const UTCtoJST = (d: string) => moment.utc(d).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');


const TURNSTILE_SITEKEY = process.env.REACT_APP_CLOUDFLARE_SITE_KEY ?? '';
const TURNSTILE_SKIP = process.env.REACT_APP_TURNSTILE_SKIP === 'true';

interface BoxInfo {
  user_id: string;
  server: string;
  display_name: string;
  avatar: string;
  reject_sensitive_word: boolean;
  loginonly: boolean;
  box_name: string;
  box_type: string | null;
  accepting: boolean;
}

interface SelfUser {
  user_id: string;
  server: string;
  display_name: string;
  profile_img: string;
}

type Target = 'direct' | 'community';

// ─── Turnstile ────────────────────────────────────────────────────────────────

const TurnstileWidget: React.FC<{
  onToken: (t: string) => void;
  onExpire: () => void;
}> = ({ onToken, onExpire }) => {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>('');
  const [blocked, setBlocked] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!TURNSTILE_SITEKEY) return;

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ts = (window as any).turnstile;
      if (!ts || !ref.current) return;
      widgetId.current = ts.render(ref.current, {
        sitekey: TURNSTILE_SITEKEY,
        callback: onToken,
        'expired-callback': onExpire,
      });
    };
    script.onerror = () => setBlocked(true);
    document.body.appendChild(script);

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (widgetId.current) (window as any).turnstile?.remove(widgetId.current);
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!TURNSTILE_SITEKEY || TURNSTILE_SKIP) return null;

  if (blocked) {
    return (
      <div className="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-[13px] text-amber-800 dark:text-amber-200">
        {t('questionBox.turnstileBlocked')}
      </div>
    );
  }

  return <div ref={ref} className="mt-2" />;
};

// ─── QuestionForm ─────────────────────────────────────────────────────────────

const QuestionForm: React.FC = () => {
  const { t } = useTranslation();
  const { user_id } = useParams<{ user_id: string }>();
  const navigate = useNavigate();

  const [boxInfo, setBoxInfo] = useState<BoxInfo | null>(null);
  const [selfUser, setSelfUser] = useState<SelfUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [target, setTarget] = useState<Target>('direct');
  const [text, setText] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [postToFediverse, setPostToFediverse] = useState(false);
  const [cfToken, setCfToken] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const loggedIn = !!Cookies.get('jwtToken');
  const charMax = 200;

  useEffect(() => {
    if (!user_id) return;
    Promise.all([
      fetch(`/api/v1/question/${user_id}`).then((r) => {
        if (!r.ok) throw r.status;
        return r.json();
      }),
      loggedIn
        ? fetch('/api/v1/profile').then((r) => (r.ok ? r.json() : null))
        : Promise.resolve(null),
    ])
      .then(([box, self]) => {
        setBoxInfo(box);
        setSelfUser(self);
      })
      .catch((status) => {
        if (status === 404 || status === 400) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [user_id, loggedIn]);

  // Force non-anonymous when targeting community
  useEffect(() => {
    if (target === 'community') {
      setAnonymous(false);
    }
  }, [target]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    if (TURNSTILE_SITEKEY && !TURNSTILE_SKIP && !cfToken) {
      setError(t('questionBox.humanCheck'));
      return;
    }
    setSending(true);
    setError('');
    try {
      const endpoint =
        target === 'direct'
          ? `/api/v1/question/${user_id}`
          : `/api/v1/community/questions`;

      const body =
        target === 'direct'
          ? { question: text, not_anonymous: !anonymous, token: cfToken || 'dev' }
          : { question: text, nsfw: false, post_to_fediverse: postToFediverse };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || t('common.error'));
        return;
      }
      setSent(true);
      setText('');
    } catch {
      setError(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingScreen />;

  if (notFound || !boxInfo) {
    return (
      <ErrorScreen
        title={t('questionBox.notFoundTitle')}
        message={t('questionBox.notFoundDesc')}
        onBack={() => navigate('/')}
      />
    );
  }

  const boxType = boxInfo.box_type ?? t('common.question');

  if (!boxInfo.accepting) {
    return (
      <ErrorScreen
        title={t('questionBox.notAcceptingTitle')}
        message={`@${boxInfo.user_id}@${boxInfo.server}`}
        onBack={() => navigate('/')}
      />
    );
  }


  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      <PublicHeader
        loggedIn={loggedIn}
        centerContent={
          loggedIn && selfUser && !anonymous ? (
            <>
              <Avatar src={selfUser.profile_img} seed={selfUser.display_name} size={20} />
              <span className="font-mono">{t('questionBox.centerSendAs', { user: `@${selfUser.user_id}@${selfUser.server}` })}</span>
            </>
          ) : (
            <>
              <Icons.Lock width={14} height={14} className="text-brand-green-500" />
              <span>{t('questionBox.centerAnonymous')}</span>
            </>
          )
        }
      />

      <div className="grid grid-cols-12 gap-8 lg:gap-10 px-4 sm:px-6 md:px-10 lg:px-16 py-10 max-w-5xl mx-auto">
        {/* Left: receiver info + checklist */}
        <div className="col-span-12 md:col-span-5">
          <div className="md:sticky md:top-8">
            {target === 'direct' && (
              <>
                <div className="flex items-center gap-4 mb-5">
                  <Avatar src={boxInfo.avatar} seed={boxInfo.display_name} size={64} />
                  <div className="min-w-0">
                    {boxInfo.box_name && (
                      <div className="text-[13px] font-semibold text-brand-blue-500 dark:text-brand-blue-300 mb-0.5">
                        {boxInfo.box_name}
                      </div>
                    )}
                    <div className="text-xl font-extrabold">{boxInfo.display_name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <a
                        href={`https://${boxInfo.server}/@${boxInfo.user_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-ink-500 font-mono truncate hover:underline"
                      >
                        @{boxInfo.user_id}@{boxInfo.server}
                      </a>
                      <ServerBadge host={boxInfo.server} />
                    </div>
                  </div>
                </div>
                <h1 className="text-[38px] leading-tight font-extrabold tracking-tight mb-3">
                  {/* @ts-ignore - Trans typings incompatible with TS 4.9 */}
                  <Trans i18nKey="questionBox.formTitle" values={{ name: boxInfo.display_name, identity: (!loggedIn || anonymous) ? t('common.anonymous') : (selfUser?.display_name ?? ''), boxType }} components={{
                    green: <span className="text-brand-green-500" />,
                    br: <br />
                  }} />
                </h1>
              </>
            )}

            <Card className="!p-5">
              <h3 className="font-bold text-[13px] mb-3 flex items-center gap-2">
                <Icons.Sparkle width={14} height={14} className="text-brand-green-500" />
                {t('questionBox.checkBeforeSend')}
              </h3>
              <ul className="space-y-2.5 text-[13px]">
                {target === 'direct' ? (
                  <>
                    <li className="flex items-start gap-2">
                      <Icons.Check width={14} height={14} className="text-brand-green-500 mt-0.5 shrink-0" />
                      <span className="text-ink-600 dark:text-ink-300">
                        {loggedIn && !anonymous
                          ? t('questionBox.checkNoAccountLoggedIn', { user: `@${selfUser?.user_id}@${selfUser?.server}` })
                          : t('questionBox.checkNoAccount')}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icons.Check width={14} height={14} className="text-brand-green-500 mt-0.5 shrink-0" />
                      <span className="text-ink-600 dark:text-ink-300">
                        {t('questionBox.checkPublic')}
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-start gap-2">
                      <Icons.Check width={14} height={14} className="text-brand-green-500 mt-0.5 shrink-0" />
                      <span className="text-ink-600 dark:text-ink-300">
                        {t('questionBox.checkCommunityPublic')}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icons.Check width={14} height={14} className="text-brand-green-500 mt-0.5 shrink-0" />
                      <span className="text-ink-600 dark:text-ink-300">
                        {t('questionBox.checkCommunityName')}
                      </span>
                    </li>
                  </>
                )}
                <li className="flex items-start gap-2 pt-2.5 border-t border-ink-150 dark:border-ink-800">
                  <div className="w-3.5 h-3.5 rounded-full bg-amber-400/30 flex items-center justify-center mt-0.5 shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  </div>
                  <span className="text-ink-600 dark:text-ink-300">
                    <span className="font-semibold text-ink-800 dark:text-ink-100">
                      {t('questionBox.checkIp')}
                    </span>
                    {t('questionBox.checkIpDesc')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-3.5 h-3.5 rounded-full bg-rose-400/30 flex items-center justify-center mt-0.5 shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  </div>
                  <span className="text-ink-600 dark:text-ink-300">
                    <span className="font-semibold text-ink-800 dark:text-ink-100">
                      {t('questionBox.checkAbuse')}
                    </span>
                    {t('questionBox.checkAbuseDesc')}
                  </span>
                </li>
              </ul>
            </Card>
          </div>
        </div>

        {/* Right: form */}
        <div className="col-span-12 md:col-span-7 md:col-start-6">
          <Card className="!p-7 relative overflow-hidden">
            <div
              className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-50"
              style={{ background: 'radial-gradient(closest-side, rgba(90,179,49,0.35), transparent)' }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-5">
                <QGlyph tone="green" size={32} />
                <h2 className="text-lg font-bold">{target === 'direct' ? t('questionBox.writeBoxType', { boxType }) : t('questionBox.writeQuestion')}</h2>
              </div>

              {/* Target selector (logged-in only for community) */}
              {loggedIn && (
                <div className="mb-5">
                  <label className="text-[12px] font-semibold text-ink-500 dark:text-ink-400 uppercase tracking-wider">
                    {t('questionBox.whoToAsk', { boxType })}
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTarget('direct')}
                      className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition text-left ${
                        target === 'direct'
                          ? 'border-brand-blue-400 bg-brand-blue-50 dark:bg-brand-blue-900/30 dark:border-brand-blue-700'
                          : 'border-ink-150 dark:border-ink-800 hover:bg-ink-100 dark:hover:bg-ink-850'
                      }`}
                    >
                      <Avatar src={boxInfo.avatar} seed={boxInfo.display_name} size={36} />
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold">{t('questionBox.toYou', { name: boxInfo.display_name })}</div>
                        <div className="text-[11px] text-ink-500 font-mono truncate">
                          @{boxInfo.user_id}@{boxInfo.server}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTarget('community')}
                      className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition text-left ${
                        target === 'community'
                          ? 'border-brand-green-400 bg-brand-green-50 dark:bg-brand-green-900/30 dark:border-brand-green-700'
                          : 'border-ink-150 dark:border-ink-800 hover:bg-ink-100 dark:hover:bg-ink-850'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-brand-green-100 dark:bg-brand-green-900/50 flex items-center justify-center text-brand-green-600 shrink-0">
                        <Icons.Globe width={18} height={18} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold">{t('questionBox.toEveryone')}</div>
                        <div className="text-[11px] text-ink-500 dark:text-ink-400">{t('questionBox.toEveryoneSub')}</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* loginonly warning */}
              {boxInfo.loginonly && !loggedIn && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-[13px] text-amber-800 dark:text-amber-200 flex items-start gap-2">
                  <Icons.Lock width={14} height={14} className="mt-0.5 shrink-0" />
                  {t('questionBox.loginOnlyWarn')}
                  <button
                    onClick={() => navigate('/login')}
                    className="underline ml-1 font-semibold"
                  >
                    {t('questionBox.loginHere')}
                  </button>
                </div>
              )}

              {/* Textarea */}
              {sent ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-14 h-14 rounded-full bg-brand-green-50 dark:bg-brand-green-900/40 flex items-center justify-center">
                    <Icons.Check width={28} height={28} className="text-brand-green-500" />
                  </div>
                  <p className="text-lg font-bold">{target === 'direct' ? t('questionBox.sendSuccessDirect', { boxType }) : t('questionBox.sendSuccessCommunity')}</p>
                  <p className="text-[13px] text-ink-500 dark:text-ink-400">
                    {t('questionBox.sendSuccessSub')}
                  </p>
                  <Btn variant="outline" size="md" onClick={() => setSent(false)}>
                    {target === 'direct' ? t('questionBox.continueDirect', { boxType }) : t('questionBox.continueCommunity')}
                  </Btn>
                </div>
              ) : (
                <>
                  <textarea
                    rows={6}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={boxInfo.loginonly && !loggedIn}
                    placeholder={
                      target === 'direct'
                        ? t('questionBox.placeholderDirect', { boxType })
                        : t('questionBox.placeholderCommunity')
                    }
                    className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-4 text-[15px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-green-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="flex justify-end text-[12px] text-ink-400 mt-1">
                    <span className={text.length > charMax ? 'text-rose-500' : ''}>{text.length}</span>
                    <span> / {charMax}</span>
                  </div>

                  {/* Fediverse post toggle (community mode) */}
                  {loggedIn && selfUser && target === 'community' && (
                    <div className="mt-5 p-4 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="font-semibold text-[13px]">{t('questionBox.postToFediverse')}</div>
                        <div className="text-[12px] text-ink-500 dark:text-ink-400">
                          {t('questionBox.postToFediverseDesc')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPostToFediverse(!postToFediverse)}
                        className={`relative w-12 h-7 rounded-full transition shrink-0 ${
                          postToFediverse ? 'bg-brand-green-500' : 'bg-ink-300 dark:bg-ink-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${
                            postToFediverse ? 'right-0.5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Identity toggle (logged-in only) */}
                  {loggedIn && selfUser && target === 'direct' && (
                    <div className="mt-5 p-4 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
                          anonymous ? 'bg-white dark:bg-ink-900' : 'bg-brand-blue-100 dark:bg-brand-blue-900/40'
                        }`}
                      >
                        {anonymous ? (
                          <Icons.Lock width={18} height={18} className="text-brand-green-500" />
                        ) : (
                          <Avatar src={selfUser.profile_img} seed={selfUser.display_name} size={28} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-[13px]">{t('questionBox.anonymousToggle')}</div>
                        <div className="text-[12px] text-ink-500 dark:text-ink-400">
                          {anonymous
                            ? t('questionBox.anonymousOnDesc')
                            : t('questionBox.anonymousOffDesc', { user: `@${selfUser.user_id}@${selfUser.server}` })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAnonymous(!anonymous)}
                        className={`relative w-12 h-7 rounded-full transition ${
                          anonymous ? 'bg-brand-green-500' : 'bg-ink-300 dark:bg-ink-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${
                            anonymous ? 'right-0.5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Turnstile */}
                  <TurnstileWidget
                    onToken={setCfToken}
                    onExpire={() => setCfToken('')}
                  />

                  {error && (
                    <p className="mt-3 text-[13px] text-rose-500">{error}</p>
                  )}

                  <div className="mt-6 flex items-center gap-3">
                    <Btn
                      variant="green"
                      size="xl"
                      className="flex-1"
                      onClick={handleSend}
                      disabled={
                        !text.trim() ||
                        text.length > charMax ||
                        sending ||
                        (boxInfo.loginonly && !loggedIn)
                      }
                    >
                      <Icons.Sparkle width={16} height={16} />
                      {sending ? t('common.sending') : target === 'direct' ? t('questionBox.sendDirect', { boxType }) : t('questionBox.sendCommunity')}
                    </Btn>
                  </div>

                  <p className="mt-4 text-center text-[11px] text-ink-500 dark:text-ink-400">
                    {t('common.agreePrefix')}
                    <a
                      href="https://gist.github.com/YoheiZuho/2fa69f3c5095bcc65319e9c89324055c"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {t('common.privacyPolicy')}
                    </a>
                    {' '}{t('common.rule')}{' '}
                    <a
                      href="https://gist.github.com/YoheiZuho/f08a8a9a1b79b794c843996739060408"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {t('common.rule')}
                    </a>
                    {t('common.agreeSuffix')}
                  </p>
                </>
              )}
            </div>
          </Card>

          {/* Public answers for this user */}
          <PublicAnswers userId={user_id!} displayName={boxInfo.display_name} />

          {/* Mastodon rel="me" verification */}
          <a rel="me" href={`https://${boxInfo.server}/@${boxInfo.user_id}`} />
        </div>
      </div>
    </div>
  );
};

// ─── PublicAnswers ─────────────────────────────────────────────────────────────

interface PublicAnswer {
  id: number;
  question: string;
  answer: string;
  answerd_at: string;
  not_anonymous: boolean;
  questioner_user_id: string;
  questioner_server: string;
}

const PREVIEW_COUNT = 5;

const PublicAnswers: React.FC<{ userId: string; displayName: string }> = ({
  userId,
  displayName,
}) => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<PublicAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetch(`/api/v1/list/answer/public/${userId}`)
      .then((r) => r.ok ? r.json() : [])
      .then((data: PublicAnswer[]) => setAnswers(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div className="mt-8 space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-20 rounded-2xl bg-ink-100 dark:bg-ink-850 animate-pulse" />
      ))}
    </div>
  );

  if (answers.length === 0) return null;

  const displayed = showAll ? answers : answers.slice(0, PREVIEW_COUNT);
  const hasMore = !showAll && answers.length > PREVIEW_COUNT;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold flex items-center gap-2">
          <QGlyph tone="blue" size={22} />
          {t('questionBox.recentAnswers', { name: displayName })}
        </h3>
        {hasMore && (
          <button
            onClick={() => setShowAll(true)}
            className="text-[12px] text-brand-blue-500 hover:underline"
          >
            {t('questionBox.recentAnswersAll')}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {displayed.map((a) => (
          <Link key={a.id} to={`/questionbox/${userId}/${a.id}`} className="block group">
            <Card className="!p-4 group-hover:border-brand-blue-300 dark:group-hover:border-brand-blue-700 transition-colors">
              <p className="text-[12px] text-ink-500 dark:text-ink-400 line-clamp-1 mb-1.5">
                Q: <TranslateText text={a.question} inline />
              </p>
              <p className="text-[14px] leading-relaxed line-clamp-2 font-medium"><TranslateText text={a.answer} inline /></p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-ink-400">
                  {a.not_anonymous && a.questioner_user_id
                    ? t('questionBox.fromUser', { user: `@${a.questioner_user_id}@${a.questioner_server}` })
                    : t('questionBox.fromAnon')}
                </span>
                <span className="text-[11px] text-ink-400">{UTCtoJST(a.answerd_at)}</span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(true)}
            className="text-[13px] text-brand-blue-500 hover:underline"
          >
            {t('questionBox.recentAnswersAll')} ({answers.length}{t('common.countAnswers', {count: answers.length})})
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionForm;
